import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PersonOutlineIcon from 'assets/icons/person_outline.svg?react';
import { Button, analyticsClient } from '@spotted-zebra-uk/ui-components';
import { HEAP_ANALYTICS_EVENTS } from '@/constants/analytics';
import { AvatarOption } from '../AvatarSelection/avatarOptions';
import Icon from '@/components/atoms/Icon';
import styles from './StepPracticePreview.module.scss';

type WaveState = 'inactive' | 'active' | 'dynamic';
type Speaker = 'agent' | 'user';

interface StepPracticePreviewProps {
  selectedAvatar: AvatarOption;
  remainingDemos: number | null;
  onStartPractice: () => void;
  onSkipToInterview: () => void;
}

export function StepPracticePreview({
  selectedAvatar,
  remainingDemos,
  onStartPractice,
  onSkipToInterview,
}: StepPracticePreviewProps) {
  const { t } = useTranslation();
  const hasRemainingDemos = true; // remainingDemos != null && remainingDemos > 0;

  return (
    <div className={styles.stepPractice}>
      <div className={styles.content}>
        <h1 className={styles.title}>
          {hasRemainingDemos
            ? t('aiInterview.wizard.tryPracticeInterview')
            : t('aiInterview.wizard.readyToStart')}
        </h1>

        {hasRemainingDemos ? (
          <DynamicPreviewCard selectedAvatar={selectedAvatar} />
        ) : (
          <StaticPreviewCard selectedAvatar={selectedAvatar} />
        )}

        {hasRemainingDemos ? (
          <p className={styles.description}>
            {t('aiInterview.wizard.practiceDescription', {
              name: selectedAvatar.name,
            })}
          </p>
        ) : (
          <div className={styles.exhaustedMessage}>
            <p className={styles.exhaustedTitle}>
              {t('aiInterview.wizard.exhaustedTitle')}
            </p>
            <p className={styles.exhaustedSubtitle}>
              {t('aiInterview.wizard.exhaustedSubtitle')}
            </p>
          </div>
        )}
      </div>

      {hasRemainingDemos && (
        <p className={styles.sessionsRemaining}>
          {t('aiInterview.wizard.notRecorded', { count: remainingDemos ?? 0 })}
        </p>
      )}

      <div className={styles.actions}>
        {hasRemainingDemos ? (
          <>
            <Button
              onClick={() => {
                analyticsClient.trackEvent({
                  name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_PRACTICE_PREVIEW_TRY_PRACTICE_CLICK,
                });
                onStartPractice();
              }}
              fullWidth
              rightIcon={<Icon icon="right_arrow" />}
            >
              {t('aiInterview.wizard.tryPracticeSession')}
            </Button>
            <p className={styles.orDivider}>{t('aiInterview.wizard.or')}</p>
            <button
              type="button"
              onClick={() => {
                analyticsClient.trackEvent({
                  name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_PRACTICE_PREVIEW_SKIP_TO_INTERVIEW_CLICK,
                });
                onSkipToInterview();
              }}
              className={styles.skipLink}
            >
              {t('aiInterview.startInterviewNow')}
            </button>
          </>
        ) : (
          <Button
            onClick={() => {
              analyticsClient.trackEvent({
                name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_PRACTICE_PREVIEW_START_INTERVIEW_CLICK,
              });
              onSkipToInterview();
            }}
            fullWidth
            rightIcon={<Icon icon="right_arrow" />}
          >
            {t('aiInterview.startInterviewNow')}
          </Button>
        )}
      </div>
    </div>
  );
}

function StaticPreviewCard({ selectedAvatar }: { selectedAvatar: AvatarOption }) {
  return (
    <div className={`${styles.previewCard} ${styles.previewCardDisabled}`}>
      <AgentParticipant
        avatar={selectedAvatar}
        speaking={false}
        waveState="active"
      />
      <DotDivider />
      <UserParticipant speaking={false} />
    </div>
  );
}

function useSpeakerLoop(videoRef: React.RefObject<HTMLVideoElement>) {
  const [speaker, setSpeaker] = useState<Speaker>('agent');
  const userTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const videoDurationRef = useRef(4);

  const startAgentPhase = useCallback(() => {
    setSpeaker('agent');
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      video.play();
    }
  }, [videoRef]);

  const handleVideoEnded = useCallback(() => {
    setSpeaker('user');
    userTimerRef.current = setTimeout(startAgentPhase, videoDurationRef.current * 1000);
  }, [startAgentPhase]);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      videoDurationRef.current = videoRef.current.duration;
    }
  }, [videoRef]);

  useEffect(() => {
    videoRef.current?.play();
    return () => clearTimeout(userTimerRef.current);
  }, [videoRef]);

  return { speaker, handleVideoEnded, handleLoadedMetadata };
}

function DynamicPreviewCard({ selectedAvatar }: { selectedAvatar: AvatarOption }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { speaker, handleVideoEnded, handleLoadedMetadata } = useSpeakerLoop(videoRef);

  const agentSpeaking = speaker === 'agent';

  return (
    <div className={styles.previewCard}>
      <AgentParticipant
        avatar={selectedAvatar}
        speaking={agentSpeaking}
        waveState={agentSpeaking ? 'dynamic' : 'inactive'}
        videoRef={videoRef}
        onVideoEnded={handleVideoEnded}
        onLoadedMetadata={handleLoadedMetadata}
      />
      <DotDivider />
      <UserParticipant speaking={!agentSpeaking} />
    </div>
  );
}

// ── Shared sub-components ────────────────────────────────────────────
function AgentParticipant({
  avatar,
  speaking,
  waveState,
  videoRef,
  onVideoEnded,
  onLoadedMetadata,
}: {
  avatar: AvatarOption;
  speaking: boolean;
  waveState: WaveState;
  videoRef?: React.RefObject<HTMLVideoElement>;
  onVideoEnded?: () => void;
  onLoadedMetadata?: () => void;
}) {
  return (
    <div className={styles.participant}>
      <div className={styles.circleContainer}>
        <div className={`${styles.avatarCircle} ${speaking ? styles.circleActive : ''} ${!speaking && videoRef ? styles.avatarCircleInactive : ''}`}>
          {videoRef ? (
            <video
              ref={videoRef}
              playsInline
              muted
              className={styles.avatarVideo}
              onEnded={onVideoEnded}
              onLoadedMetadata={onLoadedMetadata}
            >
              <source src={avatar.videoUrl} type="video/mp4" />
            </video>
          ) : (
            <img src={avatar.thumbnailUrl} alt={avatar.name} className={styles.avatarImage} />
          )}
        </div>
      </div>
      <WaveIndicator state={waveState} />
      <span className={styles.participantName}>{avatar.name}</span>
    </div>
  );
}

function UserParticipant({ speaking }: { speaking: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={styles.participant}>
      <div className={styles.circleContainer}>
        <div className={`${styles.youCircle} ${speaking ? styles.youCircleActive : ''}`}>
          <PersonOutlineIcon className={`${styles.youIcon} ${speaking ? styles.youIconActive : ''}`} />
        </div>
      </div>
      <WaveIndicator state={speaking ? 'dynamic' : 'inactive'} />
      <span className={styles.participantName}>{t('aiInterview.you')}</span>
    </div>
  );
}

function DotDivider() {
  return (
    <div className={styles.dotDivider}>
      <span />
      <span />
      <span />
    </div>
  );
}

function WaveIndicator({ state }: { state: WaveState }) {
  const heights = [14, 14, 29, 36, 18, 29, 18, 29, 14];
  const isVisible = state === 'active' || state === 'dynamic';
  return (
    <div className={styles.waveIndicator}>
      {heights.map((h, i) => (
        <div
          key={i}
          className={`${styles.wavebar} ${isVisible ? styles.wavebarActive : ''} ${state === 'dynamic' ? styles.wavebarDynamic : ''}`}
          style={{
            height: isVisible ? h : 4,
            animationDelay: state === 'dynamic' ? `${i * 0.1}s` : undefined,
          }}
        />
      ))}
    </div>
  );
}
